export const AUDIT_ACTIONS = [
  'auth.signed_in',
  'auth.sign_in_failed',
  'auth.signed_out',
  'organization.claimed',
  'invitation.created',
  'invitation.accepted',
  'invitation.revoked',
  'member.role_changed',
  'member.removed',
  'space.created',
  'space.updated',
  'space.deleted',
  'space.member_added',
  'space.member_removed',
  'space.member_role_changed',
  'file.uploaded',
  'file.processing_retried',
  'file.viewed',
  'file.downloaded',
  'file.deleted',
  'file.opened',
  'folder.created',
  'folder.updated',
  'folder.deleted',
  'deck.created',
  'deck.opened',
  'deck.updated',
  'deck.deleted',
  'deck.edited',
  'deck.exported',
  'deck.restored',
  'deck.slide_restored',
  'deck.run_reverted',
  'deck.published',
  'deck.closed',
  'published_version.updated',
  'published_version.renamed',
  'published_version.deleted',
  'published_version.link_copied',
  'workbook.created',
  'workbook.opened',
  'workbook.updated',
  'workbook.deleted',
  'workbook.restored',
  'workbook.run_reverted',
  'workbook.published',
  'sheet.edited',
  'sheet.closed',
  'session.created',
  'session.renamed',
  'session.archived',
  'session.deleted',
  'session_file.created',
  'comment.created',
  'comment.replied',
  'comment.resolved',
  'comment.deleted',
  'agent.message_sent',
] as const;

export type AuditAction = (typeof AUDIT_ACTIONS)[number];

export const AUDIT_RESOURCE_TYPES = [
  'deck',
  'workbook',
  'file',
  'folder',
  'space',
  'session',
  'comment',
  'organization',
  'member',
  'invitation',
  'published_version',
  'auth',
] as const;

export type AuditResourceType = (typeof AUDIT_RESOURCE_TYPES)[number];

export type AuditActorType = 'user' | 'agent' | 'system';

export const CLIENT_AUDIT_ACTIONS = [
  'deck.closed',
  'sheet.closed',
  'published_version.link_copied',
] as const satisfies readonly AuditAction[];

export type ClientAuditAction = (typeof CLIENT_AUDIT_ACTIONS)[number];

export interface AuditUiSource {
  ariaLabel?: string;
  component?: string;
  surface?: string;
}

export interface AuditEventDTO {
  id: string;
  organizationId: string;
  spaceId: string | null;
  resourceType: AuditResourceType;
  resourceId: string;
  action: AuditAction;
  actorType: AuditActorType;
  userId: string | null;
  actorName: string | null;
  runId: string | null;
  sessionId: string | null;
  metadata: Record<string, unknown>;
  ip: string | null;
  createdAt: string;
}

type AuditMetadata = Record<string, unknown>;

function str(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function num(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value)
    ? value
    : undefined;
}

function resourceName(m: AuditMetadata, fallback: string): string {
  return str(m.resourceName) ?? fallback;
}

function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

function truncate(value: string, max: number): string {
  return value.length > max ? `${value.slice(0, max - 1)}…` : value;
}

function editedSentence(
  m: AuditMetadata,
  fallback: string,
  unitNoun: string,
  unitCountKey: string
): string {
  const name = resourceName(m, fallback);
  const ops = num(m.operationCount);
  const units = num(m[unitCountKey]);
  const opsText = ops !== undefined ? plural(ops, 'edit') : 'edits';
  const unitsText = units ? ` across ${plural(units, unitNoun)}` : '';
  return `Made ${opsText}${unitsText} in ${name}`;
}

const SENTENCE_TEMPLATES: Record<AuditAction, (m: AuditMetadata) => string> = {
  'auth.signed_in': () => 'Signed in',
  'auth.sign_in_failed': (m) =>
    `Failed sign-in attempt${str(m.email) ? ` for ${str(m.email)}` : ''}`,
  'auth.signed_out': () => 'Signed out',
  'organization.claimed': (m) =>
    `Claimed organization ${resourceName(m, 'workspace')}`,
  'invitation.created': (m) =>
    `Invited ${str(m.email) ?? 'a user'} to the organization${str(m.role) ? ` as ${str(m.role)}` : ''}`,
  'invitation.accepted': () => 'Accepted an invitation to the organization',
  'invitation.revoked': (m) =>
    `Revoked the invitation for ${str(m.email) ?? 'a user'}`,
  'member.role_changed': (m) =>
    `Changed ${str(m.memberEmail) ?? 'a member'}'s organization role${str(m.previousRole) ? ` from ${str(m.previousRole)}` : ''}${str(m.newRole) ? ` to ${str(m.newRole)}` : ''}`,
  'member.removed': (m) =>
    `Removed ${str(m.memberEmail) ?? 'a member'} from the organization`,
  'space.created': (m) => `Created space ${resourceName(m, 'untitled')}`,
  'space.updated': (m) => `Updated space ${resourceName(m, 'settings')}`,
  'space.deleted': (m) => `Deleted space ${resourceName(m, 'untitled')}`,
  'space.member_added': (m) =>
    `Added ${str(m.email) ?? 'a member'} to the space${str(m.role) ? ` as ${str(m.role)}` : ''}`,
  'space.member_removed': (m) =>
    `Removed ${str(m.email) ?? 'a member'} from the space`,
  'space.member_role_changed': (m) =>
    `Changed ${str(m.email) ?? 'a member'}'s space role${str(m.previousRole) ? ` from ${str(m.previousRole)}` : ''}${str(m.newRole) ? ` to ${str(m.newRole)}` : ''}`,
  'file.uploaded': (m) => `Uploaded file ${resourceName(m, 'unnamed')}`,
  'file.processing_retried': (m) =>
    `Retried reading file ${resourceName(m, 'unnamed')}`,
  'file.viewed': (m) => `Viewed file ${resourceName(m, 'unnamed')}`,
  'file.downloaded': (m) => `Downloaded file ${resourceName(m, 'unnamed')}`,
  'file.deleted': (m) => `Deleted file ${resourceName(m, 'unnamed')}`,
  'file.opened': (m) => `Opened file ${resourceName(m, 'unnamed')}`,
  'folder.created': (m) => `Created folder ${resourceName(m, 'unnamed')}`,
  'folder.updated': (m) => `Updated folder ${resourceName(m, 'unnamed')}`,
  'folder.deleted': (m) => `Deleted folder ${resourceName(m, 'unnamed')}`,
  'deck.created': (m) => `Created deck ${resourceName(m, 'untitled')}`,
  'deck.opened': (m) => `Opened deck ${resourceName(m, 'untitled')}`,
  'deck.updated': (m) => `Updated deck ${resourceName(m, 'untitled')}`,
  'deck.deleted': (m) => `Deleted deck ${resourceName(m, 'untitled')}`,
  'deck.edited': (m) =>
    editedSentence(m, 'a deck', 'slide', 'affectedSlideCount'),
  'deck.exported': (m) =>
    `Exported deck ${resourceName(m, 'untitled')}${str(m.format) ? ` as ${String(m.format).toUpperCase()}` : ''}`,
  'deck.restored': (m) =>
    `Restored deck ${resourceName(m, 'untitled')} to an earlier version`,
  'deck.slide_restored': (m) =>
    `Restored a slide in deck ${resourceName(m, 'untitled')} to an earlier version`,
  'deck.run_reverted': (m) =>
    `Reverted an agent run in deck ${resourceName(m, 'untitled')}`,
  'deck.published': (m) => {
    const version = str(m.versionName);
    return `Published ${version ? `version ${version}` : 'a version'} of deck ${resourceName(m, 'untitled')}`;
  },
  'deck.closed': (m) => `Closed deck ${resourceName(m, 'untitled')}`,
  'published_version.updated': (m) =>
    `Updated published version ${resourceName(m, 'unnamed')}`,
  'published_version.renamed': (m) =>
    `Renamed a published version to ${resourceName(m, 'unnamed')}`,
  'published_version.deleted': (m) =>
    `Deleted published version ${resourceName(m, 'unnamed')}`,
  'published_version.link_copied': (m) =>
    `Copied the share link for published version ${resourceName(m, 'unnamed')}`,
  'workbook.created': (m) => `Created sheet ${resourceName(m, 'untitled')}`,
  'workbook.opened': (m) => `Opened sheet ${resourceName(m, 'untitled')}`,
  'workbook.updated': (m) => `Updated sheet ${resourceName(m, 'untitled')}`,
  'workbook.deleted': (m) => `Deleted sheet ${resourceName(m, 'untitled')}`,
  'workbook.restored': (m) =>
    `Restored sheet ${resourceName(m, 'untitled')} to an earlier version`,
  'workbook.run_reverted': (m) =>
    `Reverted an agent run in sheet ${resourceName(m, 'untitled')}`,
  'workbook.published': (m) => {
    const version = str(m.versionName);
    return `Published ${version ? `version ${version}` : 'a version'} of sheet ${resourceName(m, 'untitled')}`;
  },
  'sheet.edited': (m) =>
    editedSentence(m, 'a sheet', 'tab', 'affectedSheetCount'),
  'sheet.closed': (m) => `Closed sheet ${resourceName(m, 'untitled')}`,
  'session.created': (m) =>
    `Started chat ${resourceName(m, 'New Chat')}${str(m.documentName) ? ` in ${str(m.documentName)}` : ''}`,
  'session.renamed': (m) => `Renamed a chat to ${resourceName(m, 'unnamed')}`,
  'session.archived': (m) => `Archived chat ${resourceName(m, 'unnamed')}`,
  'session.deleted': (m) => `Deleted chat ${resourceName(m, 'unnamed')}`,
  'session_file.created': (m) =>
    `The agent saved ${resourceName(m, 'a file')} in a chat`,
  'comment.created': (m) => `Commented on ${resourceName(m, 'a document')}`,
  'comment.replied': (m) =>
    `Replied to a comment on ${resourceName(m, 'a document')}`,
  'comment.resolved': (m) =>
    `Resolved a comment thread on ${resourceName(m, 'a document')}`,
  'comment.deleted': (m) =>
    `Deleted a comment on ${resourceName(m, 'a document')}`,
  'agent.message_sent': (m) => {
    const target = resourceName(m, 'a document');
    const prompt = str(m.prompt);
    return prompt
      ? `Sent a message to the agent in ${target}: "${truncate(prompt, 140)}"`
      : `Sent a message to the agent in ${target}`;
  },
};

export function renderAuditSentence(
  action: AuditAction | string,
  metadata: Record<string, unknown> | null | undefined
): string {
  const template = SENTENCE_TEMPLATES[action as AuditAction];
  if (!template) return action;
  return template(metadata ?? {});
}
